import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import InsufficientCreditsModal from '../../components/feature/InsufficientCreditsModal';
import SEOSchema from '../../components/feature/SEOSchema';
import TemplateFillModal from './components/TemplateFillModal';
import TemplateEditorModal from './components/TemplateEditorModal';
import { useAuth } from '../../hooks/useAuth';
import { useCredits } from '../../hooks/useCredits';
import { CREDIT_COSTS } from '../../lib/supabase';
import { fillTemplate } from '../../utils/templateContent';
import {
  downloadFilledTemplate,
  downloadFilledAsPdf,
  downloadFilledAsDoc,
  getDownloadFilename,
} from '../../utils/templateDownload';

const TEMPLATES = [
  { id: '1', title: 'Professional Invoice', category: 'Invoices', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Clean, professional invoice with itemized billing and payment terms.', preview: 'https://public.readdy.ai/ai/img_res/84334cd539a3f90ce454c4242a9c5733.jpg' },
  { id: '2', title: 'Service Contract', category: 'Contracts', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Legally-structured service agreement with signature fields and terms.', preview: 'https://readdy.ai/api/search-image?query=professional%20legal%20service%20contract%20document%20template%20formal%20layout%20signature%20lines%20clean%20minimal%20design%20white%20background&width=400&height=280&seq=t2&orientation=landscape' },
  { id: '3', title: 'Modern Resume', category: 'Resumes', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'ATS-friendly resume template with clean two-column layout.', preview: 'https://readdy.ai/api/search-image?query=modern%20professional%20resume%20cv%20template%20clean%20minimal%20two%20column%20layout%20elegant%20typography%20skills%20section%20white%20background&width=400&height=280&seq=t3&orientation=landscape' },
  { id: '4', title: 'NDA Agreement', category: 'Contracts', credits: CREDIT_COSTS.PREMIUM_TEMPLATE, premium: true, description: 'Comprehensive non-disclosure agreement for business and freelancers.', preview: 'https://readdy.ai/api/search-image?query=non%20disclosure%20agreement%20NDA%20legal%20document%20template%20formal%20confidentiality%20clauses%20signature%20section%20clean%20white%20background&width=400&height=280&seq=t4&orientation=landscape' },
  { id: '5', title: 'Employee Offer Letter', category: 'HR Forms', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Professional offer letter with compensation, role, and start date fields.', preview: 'https://readdy.ai/api/search-image?query=employee%20offer%20letter%20HR%20document%20template%20with%20company%20header%20professional%20formal%20layout%20compensation%20details%20clean%20white%20background&width=400&height=280&seq=t5&orientation=landscape' },
  { id: '6', title: 'Project Checklist', category: 'Checklists', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Detailed project management checklist with milestone tracking.', preview: 'https://readdy.ai/api/search-image?query=project%20management%20checklist%20template%20document%20with%20milestone%20tracking%20checkboxes%20organized%20sections%20clean%20minimal%20design%20white%20background&width=400&height=280&seq=t6&orientation=landscape' },
  { id: '7', title: 'Business Proposal', category: 'Business Forms', credits: CREDIT_COSTS.PREMIUM_TEMPLATE, premium: true, description: 'Polished business proposal with executive summary and pricing tables.', preview: 'https://readdy.ai/api/search-image?query=professional%20business%20proposal%20document%20template%20executive%20summary%20pricing%20table%20clean%20modern%20design%20company%20letterhead%20white%20background&width=400&height=280&seq=t7&orientation=landscape' },
  { id: '8', title: 'Freelance Contract', category: 'Contracts', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Simple freelance work agreement covering scope, payment, and deliverables.', preview: 'https://readdy.ai/api/search-image?query=freelance%20contract%20agreement%20document%20template%20scope%20of%20work%20payment%20terms%20deliverables%20signature%20clean%20formal%20white%20background&width=400&height=280&seq=t8&orientation=landscape' },
  { id: '9', title: 'Expense Report', category: 'Business Forms', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Organized expense report with date, category, and total columns.', preview: 'https://readdy.ai/api/search-image?query=expense%20report%20document%20template%20with%20date%20category%20amount%20columns%20total%20calculations%20professional%20business%20clean%20white%20background&width=400&height=280&seq=t9&orientation=landscape' },
  { id: '10', title: 'Performance Review', category: 'HR Forms', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Annual employee performance review form with rating scales.', preview: 'https://readdy.ai/api/search-image?query=employee%20performance%20review%20HR%20form%20template%20rating%20scale%20evaluation%20criteria%20signature%20boxes%20professional%20clean%20white%20background&width=400&height=280&seq=t10&orientation=landscape' },
  { id: '11', title: 'Sales Invoice Bundle', category: 'Invoices', credits: CREDIT_COSTS.BUNDLE_DOWNLOAD, premium: true, description: 'Complete pack of 5 invoice styles for different business needs.', preview: 'https://readdy.ai/api/search-image?query=sales%20invoice%20bundle%20collection%20multiple%20invoice%20templates%20professional%20business%20billing%20documents%20clean%20minimal%20white%20background&width=400&height=280&seq=t11&orientation=landscape' },
  { id: '12', title: 'Meeting Agenda', category: 'Checklists', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, premium: false, description: 'Structured meeting agenda with time slots and action items.', preview: 'https://readdy.ai/api/search-image?query=meeting%20agenda%20document%20template%20time%20slots%20action%20items%20discussion%20points%20clean%20minimal%20business%20professional%20white%20background&width=400&height=280&seq=t12&orientation=landscape' },
];

const CATEGORIES = ['All', 'Invoices', 'Contracts', 'Resumes', 'HR Forms', 'Checklists', 'Business Forms'];

interface FillModalState {
  templateId: string;
  title: string;
  credits: number;
  downloadType: 'html' | 'pdf' | 'docx';
}

interface TemplateEditorState {
  templateId: string;
  title: string;
  credits: number;
}

const TemplatesPage = () => {
  const { user, refetchProfile } = useAuth();
  const { deductCredits, refreshCredits } = useCredits();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [insufficientModal, setInsufficientModal] = useState<{ show: boolean; credits: number }>({ show: false, credits: 0 });
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fillModal, setFillModal] = useState<FillModalState | null>(null);
  const [fillProcessing, setFillProcessing] = useState(false);
  const [templateEditor, setTemplateEditor] = useState<TemplateEditorState | null>(null);
  const [templateEditorProcessing, setTemplateEditorProcessing] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://workflowtools.com';

  const schema = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Professional Document Templates — WorkflowTools',
      url: `${siteUrl}/templates`,
      description: 'Browse professional document templates including invoices, contracts, resumes, HR forms, and business forms.',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'WorkflowTools' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'WorkflowTools Document Templates',
      url: `${siteUrl}/templates`,
      itemListElement: TEMPLATES.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: t.title,
        description: t.description,
        url: `${siteUrl}/templates`,
      })),
    },
  ], [siteUrl]);

  const filtered = activeCategory === 'All' ? TEMPLATES : TEMPLATES.filter((t) => t.category === activeCategory);

  /** Step 1 — open WYSIWYG editor (main button) or fill modal (small PDF/DOCX buttons) */
  const handleDownload = useCallback((
    templateId: string,
    templateCredits: number,
    templateTitle: string,
    downloadType: 'html' | 'pdf' | 'docx' = 'pdf',
  ) => {
    if (!user) { navigate('/login'); return; }
    // Main button has no suffix — open the WYSIWYG editor for all templates
    const isMainButton = !templateId.includes('_pdf') && !templateId.includes('_docx');
    if (isMainButton) {
      setTemplateEditor({ templateId, title: templateTitle, credits: templateCredits });
      return;
    }
    // Small PDF / DOCX buttons — keep the fill-form modal
    setFillModal({ templateId, title: templateTitle, credits: templateCredits, downloadType });
  }, [user, navigate]);

  /** Called when user hits Download inside the WYSIWYG editor */
  const handleTemplateEditorDownload = useCallback(async (cleanHtml: string) => {
    if (!templateEditor || !user) return;
    const { templateId, title, credits } = templateEditor;
    setTemplateEditorProcessing(true);
    setErrorMsg(null);
    try {
      const freshCredits = await refreshCredits(user.id);
      if (freshCredits < credits) {
        setTemplateEditor(null);
        setInsufficientModal({ show: true, credits });
        return;
      }
      const success = await deductCredits(user.id, credits, `template_download_${templateId}`);
      if (!success) {
        setTemplateEditor(null);
        setInsufficientModal({ show: true, credits });
        return;
      }
      downloadFilledTemplate(cleanHtml, title);
      refetchProfile();
      setSuccessMsg(`"${title}" downloaded! ${credits} credits used.`);
      setTimeout(() => setSuccessMsg(null), 3500);
      setTemplateEditor(null);
    } catch {
      setErrorMsg('Download failed. Please try again.');
      setTimeout(() => setErrorMsg(null), 4000);
    } finally {
      setTemplateEditorProcessing(false);
    }
  }, [templateEditor, user, refreshCredits, deductCredits, refetchProfile]);

  /** Step 2 — user filled the form, now deduct credits + download */
  const handleFilledDownload = useCallback(async (fieldData: Record<string, string>) => {
    if (!fillModal || !user) return;
    const { templateId, title, credits, downloadType } = fillModal;

    setFillProcessing(true);
    setErrorMsg(null);

    try {
      const freshCredits = await refreshCredits(user.id);
      if (freshCredits < credits) {
        setFillModal(null);
        setInsufficientModal({ show: true, credits });
        return;
      }

      const success = await deductCredits(user.id, credits, `template_download_${templateId}`);
      if (!success) {
        setFillModal(null);
        setInsufficientModal({ show: true, credits });
        return;
      }

      const baseId = templateId.replace(/_pdf$/, '').replace(/_docx$/, '');
      const filled = fillTemplate(baseId, fieldData);
      const ext = downloadType === 'pdf' ? 'pdf' : downloadType === 'docx' ? 'doc' : 'html';
      const filename = getDownloadFilename(title, ext);

      if (downloadType === 'pdf') {
        await downloadFilledAsPdf(filled.html, filename);
      } else if (downloadType === 'docx') {
        downloadFilledAsDoc(filled.html, filename);
      } else {
        downloadFilledTemplate(filled.html, title);
      }

      refetchProfile();
      const extLabel = downloadType === 'pdf' ? 'PDF' : downloadType === 'docx' ? 'DOCX' : 'HTML';
      setSuccessMsg(`"${title}" downloaded as ${extLabel}! ${credits} credits used.`);
      setTimeout(() => setSuccessMsg(null), 3500);
      setFillModal(null);
    } catch {
      setErrorMsg('Download failed. Please try again.');
      setTimeout(() => setErrorMsg(null), 4000);
    } finally {
      setFillProcessing(false);
    }
  }, [fillModal, user, refreshCredits, deductCredits, refetchProfile]);

  return (
    <div className="min-h-screen bg-white">
      <SEOSchema schema={schema} id="templates-schema" />
      <Navbar />

      {successMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl flex items-center gap-2 whitespace-nowrap">
          <i className="ri-checkbox-circle-line text-green-400 text-base"></i>
          {successMsg}
        </div>
      )}

      {errorMsg && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white text-sm font-medium px-5 py-3 rounded-xl flex items-center gap-2 whitespace-nowrap">
          <i className="ri-error-warning-line text-base"></i>
          {errorMsg}
        </div>
      )}

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Templates</h1>
            <p className="text-gray-500">
              Fill in your details, then download. Each action costs credits.
            </p>
          </div>

          {/* How it works banner */}
          <div className="bg-gray-50 border border-gray-100 rounded-xl px-5 py-4 mb-8 flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold">1</span>
                Click Download
              </span>
              <i className="ri-arrow-right-line text-gray-300"></i>
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold">2</span>
                Fill in your details
              </span>
              <i className="ri-arrow-right-line text-gray-300"></i>
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold">3</span>
                Get your personalized document
              </span>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((template) => (
              <div key={template.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 transition-all group">
                {/* Preview */}
                <div className="w-full h-44 overflow-hidden bg-gray-50 relative">
                  <img src={template.preview} alt={template.title} className="w-full h-full object-cover object-top" />
                  {template.premium && (
                    <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-xs"></i> Premium
                    </div>
                  )}
                  {/* Fill info badge */}
                  <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-edit-2-line text-xs"></i>
                    Personalized
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-xs text-gray-400 font-medium">{template.category}</span>
                      <h3 className="font-semibold text-gray-900 text-base mt-0.5">{template.title}</h3>
                    </div>
                    <div className="flex items-center gap-1 bg-amber-50 rounded-full px-2.5 py-1 ml-2 flex-shrink-0">
                      <i className="ri-coin-line text-amber-500 text-xs"></i>
                      <span className="text-amber-700 text-xs font-bold">{template.credits}</span>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mb-4 leading-relaxed">{template.description}</p>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleDownload(template.id, template.credits, template.title, 'html')}
                      className="w-full bg-gray-900 text-white text-sm font-medium py-2.5 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-edit-line text-amber-400 text-sm"></i>
                      Fill Out &amp; Download · {template.credits} credits
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleDownload(`${template.id}_pdf`, CREDIT_COSTS.PDF_EXPORT, template.title, 'pdf')}
                        className="bg-gray-50 text-gray-700 text-xs font-medium py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex items-center justify-center gap-1 border border-gray-100 whitespace-nowrap"
                      >
                        <i className="ri-file-pdf-line text-red-500 text-sm"></i> PDF · {CREDIT_COSTS.PDF_EXPORT}cr
                      </button>
                      <button
                        onClick={() => handleDownload(`${template.id}_docx`, CREDIT_COSTS.DOCX_EXPORT, template.title, 'docx')}
                        className="bg-gray-50 text-gray-700 text-xs font-medium py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex items-center justify-center gap-1 border border-gray-100 whitespace-nowrap"
                      >
                        <i className="ri-file-word-line text-green-600 text-sm"></i> DOCX · {CREDIT_COSTS.DOCX_EXPORT}cr
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WYSIWYG editor for all templates */}
      {templateEditor && (
        <TemplateEditorModal
          templateId={templateEditor.templateId}
          templateTitle={templateEditor.title}
          templateCredits={templateEditor.credits}
          isProcessing={templateEditorProcessing}
          onDownload={handleTemplateEditorDownload}
          onClose={() => setTemplateEditor(null)}
        />
      )}

      {/* Fill modal for all other templates */}
      {fillModal && (
        <TemplateFillModal
          templateId={fillModal.templateId.replace(/_pdf$/, '').replace(/_docx$/, '')}
          templateTitle={fillModal.title}
          templateCredits={fillModal.credits}
          downloadType={fillModal.downloadType}
          isProcessing={fillProcessing}
          onDownload={handleFilledDownload}
          onClose={() => setFillModal(null)}
        />
      )}

      {insufficientModal.show && (
        <InsufficientCreditsModal
          creditsNeeded={insufficientModal.credits}
          onClose={() => setInsufficientModal({ show: false, credits: 0 })}
        />
      )}

      <Footer />
    </div>
  );
};

export default TemplatesPage;
